import React, { useState } from 'react';
import CreatableSelect from 'react-select/creatable';
import { useTranslation } from 'react-i18next';
import { getAdminBadge } from '../utils/adminBadge';
import { isAdminOnly } from '../utils/can.js';

const MIN_CODE_LENGTH = 4;
const codeLength = (value) => `${value ?? ''}`.replace(/[^a-z0-9]/giu, '').length;

const unionOfCodes = (...lists) => {
  const byValue = new Map();
  lists.flat().forEach((option) => {
    if (!byValue.has(`${option.value}`)) byValue.set(`${option.value}`, { value: option.value, label: option.label ?? option.value });
  });
  return [...byValue.values()];
};

/**
 * The product filter of the sidebar, alongside the other filters.
 *
 * One list of codes, searched on the sides that are ticked: what the form declared in the item, and
 * what the AI read on the photo. The intelligent search shows each side as its own chip, so a code
 * can end up on one side only; here a tick applies to every code. The AI side is for administrators
 * only, as on the server.
 *
 * @param {{ item: object[], ai: object[], isAdmin: boolean, onChange: Function, styles: object }} props
 *   `onChange` receives `{ item, ai }`, the codes searched on each side
 */
const ProductFilterField = ({ item = [], ai = [], isAdmin, onChange, styles }) => {
  const { t } = useTranslation();
  const codes = unionOfCodes(item, ai);
  // What a code typed into an empty field will be searched on.
  const [pending, setPending] = useState({ item: true, ai: true });
  const searchesItem = codes.length ? item.length > 0 : pending.item;
  const searchesAi = isAdmin && (codes.length ? ai.length > 0 : pending.ai);

  const emit = (nextCodes, sides) => onChange({
    item: sides.item ? nextCodes : [],
    ai: isAdmin && sides.ai ? nextCodes : []
  });

  const toggle = (side) => {
    const sides = { item: searchesItem, ai: searchesAi };
    sides[side] = !sides[side];
    if (!sides.item && !sides.ai) return;
    setPending(sides);
    if (codes.length) emit(codes, sides);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {t('Product')}
      </label>
      <CreatableSelect
        isMulti
        value={codes}
        options={[]}
        onChange={(selected) => emit(
          (selected || []).map(option => ({ value: `${option.value}`.trim().toUpperCase(), label: `${option.label ?? option.value}`.trim().toUpperCase() })),
          { item: searchesItem, ai: searchesAi }
        )}
        isValidNewOption={input => codeLength(input) >= MIN_CODE_LENGTH}
        formatCreateLabel={input => t('Search the code “{{code}}”', { code: input.trim().toUpperCase() })}
        noOptionsMessage={() => t('Type a product code (4 characters min.)')}
        placeholder={t('Product code...')}
        components={{ DropdownIndicator: null }}
        className="text-sm"
        classNamePrefix="select"
        styles={styles}
      />
      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600 dark:text-gray-300">
        <label className="inline-flex cursor-pointer items-center gap-1.5">
          <input type="checkbox" checked={searchesItem} onChange={() => toggle('item')} />
          {t('Declared in the item')}
        </label>
        {isAdmin && (
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input type="checkbox" checked={searchesAi} onChange={() => toggle('ai')} />
            {isAdminOnly('ai.filter') && getAdminBadge()}
            {t('Seen by AI')}
          </label>
        )}
      </div>
    </div>
  );
};

export default ProductFilterField;
